import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Displays unit-of-measure label next to price, updating on variant change.
 *
 * @extends {Component<{ label: HTMLElement }>}
 */
class KilwinsUom extends Component {
  requiredRefs = ['label'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#renderFromDataset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { html, newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const updatedUom = Array.from(html?.querySelectorAll('kilwins-uom') ?? []).find(
          (element) => element.dataset.blockId === this.dataset.blockId
        );
        const responseCard = this.closest('product-card') ? html?.querySelector('product-card') : null;
        const variantId = resource?.id ?? updatedUom?.dataset.variantId ?? '';
        const uom =
          updatedUom?.dataset.defaultUom ??
          responseCard?.dataset.unitOfMeasure ??
          this.#getUomForVariant(variantId) ??
          this.#getUomFromResource(resource) ??
          '';

        if (updatedUom?.dataset.variantUoms) {
          this.dataset.variantUoms = updatedUom.dataset.variantUoms;
        }

        this.dataset.defaultUom = uom;
        this.dataset.variantId = variantId;
        this.#updateLabel(uom);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[kilwins-uom]', error);
      });
  };

  #getUomForVariant(variantId) {
    if (!variantId || !this.dataset.variantUoms) return '';

    try {
      const variantUoms = JSON.parse(this.dataset.variantUoms);
      return variantUoms[variantId] ?? variantUoms[String(variantId)] ?? '';
    } catch {
      return '';
    }
  }

  #getUomFromResource(resource) {
    const metafield = resource?.metafields?.kilwins?.unit_of_measure;
    if (!metafield) return '';

    return typeof metafield === 'string' ? metafield : metafield.value ?? '';
  }

  #renderFromDataset() {
    const uom = this.dataset.defaultUom || this.#getUomForVariant(this.dataset.variantId) || '';
    this.#updateLabel(uom);
  }

  #updateLabel(uom) {
    const { label } = this.refs;
    if (!uom) {
      label.textContent = '';
      this.hidden = true;
      return;
    }
    const prefix = this.dataset.prefix || 'per';
    label.textContent = `${prefix} ${uom}`;
    this.hidden = false;
  }
}

if (!customElements.get('kilwins-uom')) {
  customElements.define('kilwins-uom', KilwinsUom);
}
